import {
  CollectionReference,
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore'
import { useEffect } from 'react'
import useStore, { MonthlyReport } from '../store/useStore'
import { Regimen } from '../types/Regimen'
import { getEffectiveServices } from '../utils/firebaseServiceContext'
import { stopImpersonation } from '../utils/impersonationService'
import useActiveUid from './useActiveUid'

const useFetchUserData = () => {
  const user = useStore((state) => state.user)
  const identityStatus = useStore((state) => state.identityStatus)
  const activeUid = useActiveUid()

  useEffect(() => {
    if (!user) {
      useStore.setState({
        firestoreUser: undefined,
        regimens: [],
        reports: {},
      })
      return
    }
    if (
      !activeUid ||
      !['primary', 'impersonating'].includes(identityStatus)
    ) {
      return
    }

    const db = getEffectiveServices().firestore
    const unsubRegimens = onSnapshot(
      query(
        collection(db, 'regimens') as CollectionReference<Omit<Regimen, 'id'>>,
        where('uid', '==', activeUid),
      ),
      (snap) => {
        useStore.setState({
          regimens: snap.docs.map((item) => ({
            id: item.id,
            ...item.data(),
          })),
        })
      },
    )
    const unsubReports = onSnapshot(
      collection(
        db,
        `users/${activeUid}/reports`,
      ) as CollectionReference<MonthlyReport>,
      (snap) => {
        const reports: Record<string, MonthlyReport> = {}
        snap.forEach((item) => {
          reports[item.id] = item.data()
        })
        useStore.setState({ reports })
      },
    )
    const unsubUser = onSnapshot(doc(db, 'users', activeUid), (snap) => {
      const firestoreUser = snap.data()
      useStore.setState({ firestoreUser: firestoreUser as any })
      if (firestoreUser?.archived === true) {
        if (identityStatus === 'impersonating') {
          void stopImpersonation('error')
        } else {
          void getEffectiveServices().auth.signOut()
        }
      }
    })
    return () => {
      unsubUser()
      unsubRegimens()
      unsubReports()
    }
  }, [user, activeUid, identityStatus])
}

export default useFetchUserData
